import { useTranslation } from 'react-i18next';
import Button from './Button';
import Icon from './Icon';
import { SkeletonGrid } from './Skeleton';

// Error state with a retry affordance. Fetch failures used to render as an
// empty state ("No items found") — a lie when the DB is simply down.
export function ErrorState({ onRetry, message, className = '' }) {
  const { t } = useTranslation();
  return (
    <div className={`flex flex-col items-center justify-center py-16 text-center ${className}`} role="alert">
      <Icon name="warning" className="mb-4 h-12 w-12 text-red-400" strokeWidth={1.5} />
      <h3 className="text-lg font-display font-semibold text-gray-900 mb-1">
        {t('common.errorTitle', 'Something went wrong')}
      </h3>
      <p className="text-gray-600 mb-6 max-w-sm">
        {message || t('common.errorBody', "We couldn't load this right now. Please try again.")}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry', 'Try again')}
        </Button>
      )}
    </div>
  );
}

// Empty state: genuinely no data (distinct from an error).
// `icon` takes an Icon name; a node is still accepted for callers that want
// to pass their own illustration.
export function EmptyState({ icon = 'plate', title, message, action, className = '' }) {
  return (
    <div className={`flex flex-col items-center justify-center py-16 text-center ${className}`}>
      {typeof icon === 'string'
        ? <Icon name={icon} className="mb-4 h-12 w-12 text-gray-300" strokeWidth={1.5} />
        : <div className="mb-4 text-5xl" aria-hidden="true">{icon}</div>}
      {title && <h3 className="text-lg font-display font-semibold text-gray-900 mb-1">{title}</h3>}
      {message && <p className="text-gray-600 mb-6 max-w-sm">{message}</p>}
      {action}
    </div>
  );
}

// One component that resolves the loading / error / empty / ready decision so
// pages stop each reinventing (and mislabeling) it.
//
//   <DataState loading={loading} error={error} empty={items.length === 0}
//              onRetry={fetchItems} emptyProps={{ title: '...', message: '...' }}>
//     {grid}
//   </DataState>
export default function DataState({
  loading,
  error,
  empty,
  onRetry,
  children,
  skeleton,
  skeletonCount = 8,
  emptyProps = {},
}) {
  if (loading) return skeleton || <SkeletonGrid count={skeletonCount} />;
  if (error) return <ErrorState onRetry={onRetry} />;
  if (empty) return <EmptyState {...emptyProps} />;
  return children;
}
